import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { can, getDriverScope } from '../permissions';

const fmtKm = (n) => (n == null ? '—' : Number(n).toFixed(1));
const fmtTime = (t) => (t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
const todayStr = () => new Date().toISOString().split('T')[0];
const toStr = (d) => d.toISOString().split('T')[0];

const DRIVER_TYPE_OPTIONS = [
  ['', 'All Types'],
  ['delivery', 'Delivery Driver'],
  ['presalesman', 'Presalesman'],
  ['van_salesman', 'Van Salesman'],
];

function presetRange(key) {
  const now = new Date();
  const today = todayStr();
  if (key === 'today') return { from: today, to: today };
  if (key === 'week') {
    const day = now.getDay() || 7; // Mon=1..Sun=7
    const monday = new Date(now); monday.setDate(now.getDate() - day + 1);
    return { from: toStr(monday), to: today };
  }
  if (key === 'month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toStr(first), to: today };
  }
  if (key === 'lastMonth') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: toStr(first), to: toStr(last) };
  }
  return { from: today, to: today };
}

const PRESETS = [
  ['today', 'Today'],
  ['week', 'This Week'],
  ['month', 'This Month'],
  ['lastMonth', 'Last Month'],
  ['custom', 'Custom Range'],
];

const driverScope = getDriverScope();
const visibleTypeOptions = driverScope
  ? DRIVER_TYPE_OPTIONS.filter(([val]) => val === '' || driverScope.includes(val))
  : DRIVER_TYPE_OPTIONS;

export default function OdometerReport() {
  const [preset, setPreset] = useState('month');
  const [from, setFrom] = useState(presetRange('month').from);
  const [to, setTo] = useState(presetRange('month').to);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selDriver, setSelDriver] = useState('');
  const [selType, setSelType] = useState('');

  useEffect(() => {
    api.get('/drivers').then(r => setDrivers(r.data)).catch(() => {});
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== 'custom') {
      const r = presetRange(key);
      setFrom(r.from);
      setTo(r.to);
    }
  };

  const load = useCallback(() => {
    if (!from || !to) return;
    setLoading(true);
    const driverParam = selDriver ? `&driver_id=${selDriver}` : '';
    const typeParam = selType ? `&type=${selType}` : '';
    api.get(`/analytics/odometer-report?from=${from}&to=${to}${driverParam}${typeParam}`)
      .then(r => { setRows(r.data.rows); setTotals(r.data.totals); })
      .catch(() => toast.error('Could not load report'))
      .finally(() => setLoading(false));
  }, [from, to, selDriver, selType]);

  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const driverParam = selDriver ? `&driver_id=${selDriver}` : '';
      const typeParam = selType ? `&type=${selType}` : '';
      const res = await api.get(`/analytics/odometer-report/export?from=${from}&to=${to}${driverParam}${typeParam}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `odometer-report-${from}-to-${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!can('odometer_report', 'view')) {
    return <div style={{ padding: 24 }}>You don't have access to this page.</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 16 }}>Odometer Report</h1>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        {PRESETS.map(([key, label]) => (
          <button key={key} onClick={() => applyPreset(key)}
            style={{
              padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', cursor: 'pointer',
              background: preset === key ? '#0284c7' : '#fff',
              color: preset === key ? '#fff' : '#334155', fontWeight: 600, fontSize: 13,
            }}>
            {label}
          </button>
        ))}
        {preset === 'custom' && (
          <>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              style={{ padding: 7, borderRadius: 8, border: '1px solid #cbd5e1' }} />
            <span>to</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              style={{ padding: 7, borderRadius: 8, border: '1px solid #cbd5e1' }} />
          </>
        )}
        <select value={selType} onChange={e => setSelType(e.target.value)}
          style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, color: '#334155' }}>
          {visibleTypeOptions.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
        </select>
        <select value={selDriver} onChange={e => setSelDriver(e.target.value)}
          style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, color: '#334155' }}>
          <option value="">All Drivers</option>
          {drivers
            .filter(d => !driverScope || driverScope.includes(d.type))
            .filter(d => !selType || d.type === selType)
            .map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button onClick={exportXlsx} disabled={exporting || !rows.length}
          style={{ marginLeft: 'auto', padding: '8px 14px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          {exporting ? 'Exporting…' : '⬇ Export Excel'}
        </button>
      </div>

      {totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            ['Sessions', totals.sessions],
            ['With Plan', totals.with_plan],
            ['Without Plan', totals.without_plan],
            ['Total Odo KM', fmtKm(totals.total_odo_km)],
            ['Total GPS KM', fmtKm(totals.total_gps_km)],
          ].map(([label, val]) => (
            <div key={label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              {['Date', 'Driver', 'Plan', 'Salesperson(s)', 'Start Time', 'End Time', 'Opening Odo', 'Closing Odo', 'Odo KM', 'GPS KM', 'Variance', 'Invoices'].map(h => (
                <th key={h} style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>}
            {!loading && !rows.length && <tr><td colSpan={12} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No sessions in this range</td></tr>}
            {rows.map(r => (
              <tr key={r.session_id} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '9px 12px' }}>{r.session_date?.slice(0, 10)}</td>
                <td style={{ padding: '9px 12px', fontWeight: 600 }}>{r.driver_name || '—'}</td>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                    background: r.plan_type === 'with_plan' ? '#dcfce7' : '#fef3c7',
                    color: r.plan_type === 'with_plan' ? '#166534' : '#92400e',
                  }}>
                    {r.plan_type === 'with_plan' ? 'With Plan' : 'Without Plan'}
                  </span>
                </td>
                <td style={{ padding: '9px 12px', color: '#475569' }}>{r.salesmen || '—'}</td>
                <td style={{ padding: '9px 12px' }}>{fmtTime(r.started_at)}</td>
                <td style={{ padding: '9px 12px' }}>{fmtTime(r.route_end)}</td>
                <td style={{ padding: '9px 12px' }}>{r.odo_start ?? '—'}</td>
                <td style={{ padding: '9px 12px' }}>{r.odo_end ?? '—'}</td>
                <td style={{ padding: '9px 12px' }}>{fmtKm(r.odo_km)}</td>
                <td style={{ padding: '9px 12px' }}>{fmtKm(r.gps_km)}</td>
                <td style={{ padding: '9px 12px', color: (r.odo_km != null && Math.abs(r.odo_km - r.gps_km) > 5) ? '#dc2626' : '#64748b' }}>
                  {r.odo_km != null ? fmtKm(r.odo_km - r.gps_km) : '—'}
                </td>
                <td style={{ padding: '9px 12px' }}>{r.invoice_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
